import { Truck, CheckCircle2, AlertTriangle } from "lucide-react";

/**
 * Compute a single pickup-status for an order from its dockets.
 *
 * Returns one of:
 *   "none"      — order has no dockets attached (nothing to pick up).
 *   "awaiting"  — at least one docket is still Pending or Scheduled.
 *   "complete"  — every docket is Picked Up.
 *   "cancelled" — all dockets are Cancelled (rare, treat as no-pickup-needed).
 */
export function computePickupStatus(orderId, allDockets) {
  const dockets = (allDockets || []).filter((d) => d.Order_ID === orderId);
  if (!dockets.length) return "none";
  const statuses = new Set(dockets.map((d) => d.Docket_Status));
  if ([...statuses].every((s) => s === "Cancelled")) return "cancelled";
  const allDone = dockets.every((d) => d.Docket_Status === "Picked Up" || d.Docket_Status === "Cancelled");
  if (allDone) return "complete";
  return "awaiting";
}

/** Days since an order was invoiced — used by the "stalled invoiced" dashboard tile. */
export function daysSinceInvoiced(order) {
  const iso = order?.Invoiced_Date || order?.Completed_At;
  if (!iso) return null;
  const ms = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(ms) || ms < 0) return null;
  return Math.floor(ms / 86400000);
}

const TONE = {
  awaiting:  { cls: "bg-amber-100 text-amber-800 border-amber-200",   Icon: Truck,          text: "Awaiting pickup" },
  complete:  { cls: "bg-emerald-100 text-emerald-800 border-emerald-200", Icon: CheckCircle2, text: "Picked up" },
  cancelled: { cls: "bg-zinc-100 text-zinc-700 border-zinc-200",      Icon: AlertTriangle,  text: "Docket cancelled" },
  none:      { cls: "bg-zinc-100 text-zinc-600 border-zinc-200",      Icon: AlertTriangle,  text: "No docket" },
};

/** Small badge for use on order cards (Pipeline Completed + Invoicing kanban). */
export default function PickupStatusBadge({ status, days, className = "" }) {
  const tone = TONE[status];
  if (!tone) return null;
  const overdue = status === "awaiting" && typeof days === "number" && days >= 2;
  return (
    <span
      data-testid={`pickup-status-${status}`}
      className={[
        "inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border whitespace-nowrap",
        tone.cls,
        overdue ? "ring-2 ring-red-300" : "",
        className,
      ].join(" ")}
      title={overdue ? `Invoiced ${days} day(s) ago — pickup still pending` : tone.text}
    >
      <tone.Icon className="w-3 h-3" />
      {overdue ? `${tone.text} · ${days}d` : tone.text}
    </span>
  );
}
